import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const services = [
  {
    title: 'Barber',
    description: 'Sharp cuts, clean fades, beard shaping, and grooming for everyday confidence.',
    detail: 'Barber services cover clean haircuts, fades, lineups, beard shaping, shaving, and grooming finishes. A provider will usually begin with a quick style check, shape the cut around your face and routine, then finish with detail work so the look feels sharp after you leave.',
    flow: ['Choose a barber or grooming provider', 'Pick the exact service and time', 'Confirm your booking and show up ready'],
    image: 'https://images.unsplash.com/photo-1503951914875-452162b0f3f1?auto=format&fit=crop&q=80&w=1000',
    category: 'barber',
  },
  {
    title: 'Styling',
    description: 'Blowouts, silk presses, braids, color styling, and looks shaped around your texture.',
    detail: 'Styling services help you get a polished everyday look or something more expressive for an event. Providers may offer blowouts, braids, wig installs, silk presses, color styling, and hair treatments depending on their menu.',
    flow: ['Browse stylists by specialty', 'Select the service that fits your hair goal', 'Share notes or inspiration before your appointment'],
    image: 'https://images.unsplash.com/photo-1562322140-8baeececf3df?auto=format&fit=crop&q=80&w=1000',
    category: 'hair',
  },
  {
    title: 'Tattoo',
    description: 'Fine-line, custom, symbolic, and statement pieces from trusted tattoo artists.',
    detail: 'Tattoo services are usually custom to your idea, placement, size, and detail level. The artist may discuss references, sizing, skin placement, prep, and aftercare before starting the session.',
    flow: ['Review artist portfolios carefully', 'Book a consultation or tattoo slot', 'Follow prep and aftercare guidance'],
    image: 'https://images.unsplash.com/photo-1598371839696-5c5bb00bdc28?auto=format&fit=crop&q=80&w=1000',
    category: 'tattoo',
  },
  {
    title: 'Massage',
    description: 'Relaxation, deep tissue, recovery, and wellness sessions for body reset days.',
    detail: 'Massage services can focus on relaxation, stress relief, muscle recovery, or targeted tension. Providers list session length, technique, location options, and any add-ons they offer.',
    flow: ['Choose a massage type and duration', 'Pick in-shop or mobile if available', 'Arrive a few minutes early and relax'],
    image: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&q=80&w=1000',
    category: 'massage',
  },
  {
    title: 'Piercing',
    description: 'Ear, nose, and body piercing with hygienic care and styling guidance.',
    detail: 'Piercing services include placement guidance, jewelry selection, hygienic piercing, and aftercare instructions. A good provider will explain healing time and how to care for the piercing afterward.',
    flow: ['Confirm the piercing type and jewelry', 'Book with a hygiene-focused provider', 'Follow aftercare until fully healed'],
    image: 'https://images.unsplash.com/photo-1605980625600-88f7f0fb58c4?auto=format&fit=crop&q=80&w=1000',
    category: 'piercing',
  },
  {
    title: 'Makeup',
    description: 'Soft glam, bridal, editorial, and event-ready makeup for every skin tone.',
    detail: 'Makeup services range from natural soft glam to full bridal and editorial looks. Providers may ask about your skin type, event, preferred finish, outfit colors, and photo references.',
    flow: ['Pick a makeup artist and look type', 'Share your event details or inspiration', 'Book a slot with enough prep time'],
    image: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?auto=format&fit=crop&q=80&w=1000',
    category: 'makeup',
  },
  {
    title: 'Nails',
    description: 'Manicures, pedicures, gel polish, nail art, and clean finishing care.',
    detail: 'Nail services include clean manicures, pedicures, gel polish, acrylics, extensions, and nail art. You can compare providers by style, hygiene, photos, pricing, and appointment length.',
    flow: ['Choose the nail style or treatment', 'Add design notes if needed', 'Book and arrive with clean hands or feet'],
    image: 'https://images.unsplash.com/photo-1604654894610-df63bc536371?auto=format&fit=crop&q=80&w=1000',
    category: 'nails',
  },
];

const Services = () => {
  const trackRef = useRef(null);
  const tweenRef = useRef(null);
  const modalRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedService, setSelectedService] = useState(null);
  const loopedServices = [...services, ...services];

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const ctx = gsap.context(() => {
      tweenRef.current = gsap.to(track, {
        xPercent: -50,
        duration: 36,
        ease: 'none',
        repeat: -1,
      });

      gsap.fromTo(
        '.service-slide',
        { autoAlpha: 0, y: 34 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.7,
          ease: 'power3.out',
          stagger: 0.08,
          scrollTrigger: null,
        }
      );
    }, track);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (!selectedService) return undefined;

    pause();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.service-info-modal',
        { autoAlpha: 0, y: 20, scale: 0.96 },
        { autoAlpha: 1, y: 0, scale: 1, duration: 0.32, ease: 'power3.out' }
      );
      gsap.fromTo(
        '.service-info-modal__item',
        { autoAlpha: 0, y: 8 },
        { autoAlpha: 1, y: 0, duration: 0.26, stagger: 0.06, ease: 'power2.out', delay: 0.08 }
      );
    }, modalRef);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setSelectedService(null);
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      ctx.revert();
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      resume();
    };
  }, [selectedService]);

  const pause = () => tweenRef.current?.pause();
  const resume = () => tweenRef.current?.resume();

  const goTo = (direction) => {
    const nextIndex = (activeIndex + direction + services.length) % services.length;
    setActiveIndex(nextIndex);
    const cardWidth = trackRef.current?.querySelector('.service-slide')?.getBoundingClientRect().width || 340;
    gsap.to(trackRef.current, {
      x: -(cardWidth + 24) * nextIndex,
      duration: 0.55,
      ease: 'power3.out',
      onStart: pause,
      onComplete: resume,
    });
  };

  return (
    <div className="overflow-hidden bg-white px-4 py-10 sm:px-6 sm:py-20 md:py-24 lg:px-8 lg:py-28">
      <div className="max-w-7xl mx-auto">
        <div className="mb-7 flex flex-col gap-4 sm:mb-14 sm:gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] sm:mb-4 sm:px-3 sm:text-xs sm:tracking-[0.16em]"
              style={{ backgroundColor: 'var(--orange-2)', color: 'var(--orange-9)' }}
            >
              <SparklesIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Services
            </div>
            <h2 className="mb-3 text-left text-2xl font-bold leading-tight text-gray-900 sm:mb-4 sm:text-4xl md:text-5xl">
              These are the services that we provide
            </h2>
            <p className="max-w-2xl text-left text-sm leading-6 text-gray-600 sm:text-lg">
              Choose from trusted professionals across grooming, styling, wellness, body art, and beauty care.
            </p>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => goTo(-1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border transition-colors sm:h-11 sm:w-11"
              style={{ borderColor: 'var(--gray-5)', color: 'var(--gray-12)' }}
              aria-label="Previous service"
            >
              <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
            <button
              type="button"
              onClick={() => goTo(1)}
              className="flex h-9 w-9 items-center justify-center rounded-full text-white transition-colors sm:h-11 sm:w-11"
              style={{ backgroundColor: 'var(--orange-9)' }}
              aria-label="Next service"
            >
              <ArrowRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>

        <div
          className="relative -mx-4 overflow-hidden px-4 sm:-mx-6 sm:px-6"
          style={{
            WebkitMaskImage:
              'linear-gradient(to right, transparent 0%, black 7%, black 93%, transparent 100%)',
            maskImage:
              'linear-gradient(to right, transparent 0%, black 7%, black 93%, transparent 100%)',
          }}
          onMouseEnter={pause}
          onMouseLeave={resume}
          onFocus={pause}
          onBlur={resume}
        >
          <div ref={trackRef} className="flex w-max gap-6 will-change-transform">
            {loopedServices.map((service, idx) => (
              <article
                key={`${service.title}-${idx}`}
                className="service-slide group relative h-[292px] w-[72vw] max-w-[238px] overflow-hidden rounded-2xl bg-gray-100 shadow-lg sm:h-[420px] sm:w-[360px] sm:max-w-[360px] sm:rounded-[22px]"
              >
                <img
                  src={service.image}
                  alt={`${service.title} service`}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading={idx < services.length ? 'eager' : 'lazy'}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/78 via-black/28 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4 text-white sm:p-6">
                  <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white/70 sm:mb-3 sm:text-xs sm:tracking-[0.18em]">
                    {String((idx % services.length) + 1).padStart(2, '0')}
                  </p>
                  <h3 className="mb-2 text-left text-lg font-bold text-white sm:mb-3 sm:text-2xl">
                    {service.title}
                  </h3>
                  <p className="mb-3 text-[11px] leading-4 text-white/82 sm:mb-5 sm:text-sm sm:leading-6">
                    {service.description}
                  </p>
                  <button
                    type="button"
                    onClick={() => setSelectedService(service)}
                    className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold text-white transition-colors sm:px-4 sm:py-2 sm:text-sm"
                    style={{ backgroundColor: 'var(--orange-9)', border: 'none' }}
                  >
                    Read more
                    <ArrowRightIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>

      {selectedService && (
        <div
          ref={modalRef}
          className="fixed inset-0 z-[80] flex items-center justify-center px-3 py-4 sm:px-4 sm:py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="service-info-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/35 backdrop-blur-md"
            aria-label="Close service details"
            onClick={() => setSelectedService(null)}
          />
          <div className="service-info-modal relative max-h-[calc(100dvh-32px)] w-full max-w-[330px] overflow-y-auto rounded-2xl bg-white text-left shadow-2xl sm:max-w-[420px]">
            <div className="relative h-28 overflow-hidden sm:h-36">
              <img
                src={selectedService.image}
                alt=""
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <button
                type="button"
                onClick={() => setSelectedService(null)}
                className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/92 text-gray-900 shadow-sm"
                aria-label="Close service details"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
              <div className="absolute bottom-3 left-4 right-4 text-white sm:bottom-4">
                <p className="service-info-modal__item mb-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white/75">
                  Service guide
                </p>
                <h3 id="service-info-title" className="service-info-modal__item m-0 text-left text-xl font-bold text-white sm:text-2xl">
                  {selectedService.title}
                </h3>
              </div>
            </div>

            <div className="p-4 sm:p-5">
              <p className="service-info-modal__item m-0 text-xs leading-5 text-gray-600 sm:text-sm sm:leading-6">
                {selectedService.detail}
              </p>

              <div className="service-info-modal__item mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3 sm:mt-5 sm:p-4">
                <p className="m-0 text-xs font-bold uppercase tracking-[0.12em]" style={{ color: 'var(--orange-9)' }}>
                  How it works
                </p>
                <ol className="mt-3 space-y-2.5 p-0 sm:space-y-3" style={{ listStyle: 'none' }}>
                  {selectedService.flow.map((item, index) => (
                    <li key={item} className="flex items-start gap-3">
                      <span
                        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
                        style={{ backgroundColor: 'var(--orange-2)', color: 'var(--orange-9)' }}
                      >
                        {index + 1}
                      </span>
                      <span className="text-xs leading-5 text-gray-700 sm:text-sm">{item}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <button
                type="button"
                onClick={() => setSelectedService(null)}
                className="service-info-modal__item mt-5 w-full rounded-full px-4 py-2.5 text-sm font-semibold text-white"
                style={{ backgroundColor: 'var(--orange-9)', border: 'none' }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
